import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Modal } from 'react-bootstrap';

import CreatePropertyForm, { AB_SEGMENTS } from './create_property_form';
import SuggestedListingForm from './suggested_listing_form';
import { countryListPropType } from './prop_types';
import { post } from 'helpers/api';
import { pick } from 'lodash';

export function NewPropertyFromScratch({
  allowUploadDoc,
  buttonAbSegment,
  formFields,
  updateFormFields,
  countryList,
  errorMessages,
  gotoPrevStep,
  showCreateDocButton,
  hasErrors,
  hasInsights,
  maxBrokersLimit,
  saving,
  showBrokerSelect,
  onSave,
  onAiUploadStatusChange
}) {
  const MODAL_STEPS = {
    NEW_LISTING: 'new_listing',
    SUGGESTED_LISTING: 'suggested_listing',
    LISTING_NOT_FOUND: 'listing_not_found'
  };
  const [brokers, setBrokers] = useState([{}]);

  const [modalStatus, setModalStatus] = useState(hasInsights ? MODAL_STEPS.NEW_LISTING : MODAL_STEPS.LISTING_NOT_FOUND);
  const [fullAddress, setFullAddress] = useState('');
  const [suggestedAddresses, setSuggestedAddresses] = useState([]);
  const [checkingInsights, setCheckingInsights] = useState(false);

  function updateForm(changes) {
    if (changes.country && !changes.state) {
      changes.state = changes.country.countryStates[0]?.code || '';
      changes.zip = '';
    }

    for (const key in changes) {
      updateFormFields(key, changes[key]);
    }
  }

  const buildFullAddress = ({ address, city, state, zip }) => `${address}, ${city}, ${state} ${zip || ''}`;

  const handlePropertySearch = async () => {
    setCheckingInsights(true);
    const body = {
      insights_property: pick(formFields, ['address', 'city', 'state', 'zip']),
      only: ['address', 'city', 'state', 'zip', 'propertyId']
    };
    const insightResponse = await post('/insights_properties/search', body);
    const builtAddress = buildFullAddress(formFields);
    setFullAddress(builtAddress);

    if (insightResponse['total'] === 0) {
      setModalStatus(MODAL_STEPS.LISTING_NOT_FOUND);
      createPropertyListing();
    } else {
      const records = insightResponse['hits'].map((record) => {
        return { fullAddress: buildFullAddress(record), id: record.propertyId };
      });
      setSuggestedAddresses(records);
      setModalStatus(MODAL_STEPS.SUGGESTED_LISTING);
    }
    setCheckingInsights(false);
  };

  const returnFromAddressCorrection = () => {
    setModalStatus(MODAL_STEPS.NEW_LISTING);
  };

  const isNewListing = modalStatus === MODAL_STEPS.NEW_LISTING;
  const isListingNotFound = modalStatus === MODAL_STEPS.LISTING_NOT_FOUND;
  const isSuggestedListing = modalStatus === MODAL_STEPS.SUGGESTED_LISTING;

  const researchPropertyParams = {
    ...pick(formFields, ['address', 'city', 'state', 'zip', 'county', 'building_size', 'building_class']),
    country_id: formFields.country?.id,
    property_type_id: formFields.property_type_id,
  };
  const propertyParams = {
    sale_checked: formFields.sale_or_lease === 'sale',
    lease_checked: formFields.sale_or_lease === 'lease',
    name: formFields.name,
    location_description: formFields.location_description
  };

  const createPropertyListing = (extraParams = {}) => {
    onSave({
      brokerIds: brokers.map(broker => broker.value),
      propertyParams,
      researchPropertyParams,
      aiFeedback: formFields.aiFeedback,
      aiIngestData: formFields.aiIngestMetadata,
      ...extraParams
    });
  };

  const createPropertyListingWithDocument = () => {
    createPropertyListing({ createDocument: true });
  };

  const suggestedPropertyListing = (params) => {
    onSave({
      brokerIds: brokers.map(broker => broker.value),
      aiFeedback: formFields.aiFeedback,
      aiIngestData: formFields.aiIngestMetadata,
      propertyParams,
      researchPropertyParams,
      ...params
    });
  };

  return (
    // NOTE: `key` forces a remount when modalStatus changes, resetting its state
    <Modal.Body className="modal-padding">
      {(isNewListing || isListingNotFound) && (
        <CreatePropertyForm
          addressNotFound={fullAddress}
          allowUploadDoc={allowUploadDoc}
          brokers={brokers}
          buttonAbSegment={buttonAbSegment}
          countryList={countryList}
          errorMessages={errorMessages}
          formFields={formFields}
          gotoPrevStep={gotoPrevStep}
          showCreateDocButton={showCreateDocButton}
          hasErrors={hasErrors}
          hasInsights={hasInsights}
          key={modalStatus}
          listingNotFound={isListingNotFound}
          maxBrokersLimit={maxBrokersLimit}
          newListing={isNewListing}
          saving={saving || checkingInsights}
          showBrokerSelect={showBrokerSelect}
          onAiUploadStatusChange={onAiUploadStatusChange}
          onBrokerChange={setBrokers}
          onChange={updateForm}
          onFormSubmit={isNewListing ? handlePropertySearch : createPropertyListing}
          onFormSubmitWithDocument={createPropertyListingWithDocument}
        />
      )}
      {isSuggestedListing && (
        <SuggestedListingForm
          backButtonClicked={returnFromAddressCorrection}
          brokers={brokers}
          errorMessages={errorMessages}
          fullAddress={fullAddress}
          hasErrors={hasErrors}
          maxBrokersLimit={maxBrokersLimit}
          saving={saving}
          showBrokerSelect={showBrokerSelect}
          suggestedAddresses={suggestedAddresses}
          suggestedPropertyListing={suggestedPropertyListing}
          userEnteredFields={researchPropertyParams}
          onBrokerChange={setBrokers}
        />
      )}
    </Modal.Body>
  );
}

NewPropertyFromScratch.propTypes = {
  allowUploadDoc: PropTypes.bool,
  buttonAbSegment: PropTypes.oneOf(Object.values(AB_SEGMENTS)),
  countryList: countryListPropType.isRequired,
  errorMessages: PropTypes.object,
  formFields: PropTypes.shape({
    address: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string,
    country_id: PropTypes.number
  }),
  gotoPrevStep: PropTypes.func,
  showCreateDocButton: PropTypes.bool,
  hasErrors: PropTypes.bool.isRequired,
  hasInsights: PropTypes.bool.isRequired,
  maxBrokersLimit: PropTypes.number.isRequired,
  saving: PropTypes.bool.isRequired,
  showBrokerSelect: PropTypes.bool.isRequired,
  updateFormFields: PropTypes.func.isRequired,
  onAiUploadStatusChange: PropTypes.func,
  onSave: PropTypes.func.isRequired,
};


const mapStateToProps = state => ({
  formFields: state.newPropertyFromScratchLocation || {}
});

const mapDispatchToProps = dispatch => ({
  updateFormFields: (fieldName, value) => {
    return dispatch({ type: `change_${fieldName}`, [fieldName]: value });
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(NewPropertyFromScratch);
